import type { IResponseDataList, IStandardResponse } from '@/entities/common/types/api';
import type { IBreadcrumbs } from '@/entities/common/types/breadcrumbs';
import type { ICity } from '@/entities/common/types/city';
import type { JSONSchema } from '@/entities/common/types/jsonSchema';
import type { ISEOData } from '@/entities/common/types/seo';
import type { Skill } from '@/entities/skill/model/types';

import type { HttpClient } from '../http';

class MainRepository {
  private http: HttpClient;
  private skillsCache: Map<string, Skill[]> = new Map();

  constructor(http: HttpClient) {
    this.http = http;
  }

  public async getCities(): Promise<ICity[]> {
    try {
      const data = await this.http.get<IResponseDataList<ICity>>(`/references/cities`);

      return data.data;
    } catch (error) {
      console.error(error);

      return [];
    }
  }

  public async getBreadcrumbs(url: string): Promise<IBreadcrumbs[]> {
    try {
      const data = await this.http.get<IResponseDataList<IBreadcrumbs>>(`/breadcrumbs`, {}, { url });

      return data.data;
    } catch (error) {
      console.error(error);

      return [];
    }
  }

  public async getSEODataByURL(url: string): Promise<ISEOData | null> {
    try {
      const data = await this.http.get<IStandardResponse<ISEOData>>(`/hat`, {}, { url });

      return data.data;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  public async getSEOSchema(url: string): Promise<JSONSchema[]> {
    try {
      const data = await this.http.get<IResponseDataList<JSONSchema>>(`/json-ld`, {}, { url });

      return data.data;
    } catch (error) {
      console.error(error);

      return [];
    }
  }

  public async getAllSkills(): Promise<Skill[]> {
    const cacheKey = 'all-skills';

    if (this.skillsCache.has(cacheKey)) {
      return this.skillsCache.get(cacheKey)!;
    }

    try {
      const response = await this.http.get<IResponseDataList<Skill>>('/references/skills');
      const skills = response.data;

      this.skillsCache.set(cacheKey, skills);
      return skills;
    } catch (error) {
      console.error('Error fetching skills:', error);
      return [];
    }
  }
}

export { MainRepository };
